import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import Layout from "layouts/DashboardManage";
import axios from "helpers/_axios";
import Notification, { notifyApiError } from "components/Notification";
import { SpinnerSyncBlock, SpinnerRainbow } from "components/Spinner";
import ManageDataTable from "components/ManageDataTable";
import NoData from "components/NoData";
import Translate from "helpers/Translate";
import AiChipIcon from "./AiChipIcon";

import "./Ai.styles.scss";

function AiCredentials() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadModels = useCallback(async () => {
    try {
      setLoading(true);
      const result = await axios().get("/manage-ai", { params: { nopage: 1 } });
      setModels(result.data?.data || []);
    } catch (error) {
      notifyApiError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const handleActivate = async id => {
    try {
      const modelId = id != null ? String(id) : "";
      if (!modelId) {
        Notification.error("AI model not found");
        return;
      }
      const result = await axios().put(`/manage-ai/${modelId}/activate`);
      const activated = result.data?.data;
      Notification.success(Translate({ id: "ai_model_activated_successfully" }));
      if (activated && activated.id) {
        setModels(prev =>
          prev.map(item => ({
            ...item,
            is_active: String(item.id) === String(activated.id)
          }))
        );
      }
      await loadModels();
    } catch (error) {
      notifyApiError(error);
    }
  };

  const handleDelete = item => {
    Notification.confirm(
      Translate({
        id: "sure_to_delete",
        values: [item.model || item.provider || "AI model"]
      }),
      {
        onOk: async () => {
          try {
            await axios().delete(`/manage-ai/${item.id}`);
            Notification.success(
              Translate({ id: "ai_model_deleted_successfully" })
            );
            await loadModels();
          } catch (error) {
            notifyApiError(error);
          }
        }
      }
    );
  };

  return (
    <Layout>
      {loading && !models.length && <SpinnerSyncBlock />}
      <div className="manage ai-credentials">
        <Link to="/manage" className="back mb-20">
          <i className="fas fa-long-arrow-alt-left"></i>
          {Translate({ id: "back_to_manage_dashboard" })}
        </Link>

        <div className="manage-page-heading">
          <span
            className="manage-page-icon tone-teal ai-page-icon"
            aria-hidden="true"
          >
            <AiChipIcon className="ai-page-icon__svg" />
          </span>
          <div className="manage-page-heading__text">
            <h1 className="title">
              {Translate({ id: "manage_ai_credentials" })}
            </h1>
            <p className="manage-page-subtitle">
              {Translate({ id: "manage_ai_credentials_description" })}
            </p>
          </div>
        </div>

        <div className="manage-list-actions manage-list-actions--end">
          <Link
            className="btn btn-primary btn-add-manage"
            to="/manage/ai/add"
          >
            <i className="fas fa-plus mr-10"></i>
            {Translate({ id: "add_ai_model" })}
          </Link>
        </div>

        {loading && <SpinnerRainbow />}

        {!loading && (
          <ManageDataTable>
            <table>
              <thead>
                <tr>
                  <th className="left">{Translate({ id: "ai_provider" })}</th>
                  <th className="left">{Translate({ id: "ai_model" })}</th>
                  <th className="left">{Translate({ id: "ai_api_key" })}</th>
                  <th className="left">
                    {Translate({ id: "lens_ai_domain" })}
                  </th>
                  <th className="left">{Translate({ id: "ollama_url" })}</th>
                  <th className="left">{Translate({ id: "status" })}</th>
                  <th className="action-col">{Translate({ id: "action" })}</th>
                </tr>
              </thead>
              <tbody>
                {models.length > 0 ? (
                  models.map(item => (
                    <tr key={item.id}>
                      <td className="left">{item.provider || "--"}</td>
                      <td className="left">{item.model || "--"}</td>
                      <td className="left">
                        {item.provider === "ollama"
                          ? "--"
                          : item.api_key_set
                          ? item.api_key_masked
                          : Translate({ id: "no_api_key_saved_yet" })}
                      </td>
                      <td className="left">{item.lens_ai_domain || "--"}</td>
                      <td className="left">
                        {item.provider === "ollama"
                          ? item.ollama_url || "--"
                          : "--"}
                      </td>
                      <td className="left">
                        {item.is_active ? (
                          <span className="ai-status-badge ai-status-badge--active">
                            {Translate({ id: "active_model" })}
                          </span>
                        ) : (
                          <span className="ai-status-badge">
                            {Translate({ id: "inactive" })}
                          </span>
                        )}
                      </td>
                      <td className="action-col">
                        <ReactTooltip />
                        <div className="manage-data-table-actions">
                          {!item.is_active && (
                            <button
                              type="button"
                              className="action-btn action-secondary"
                              data-tip={Translate({ id: "set_as_active" })}
                              onClick={() => handleActivate(item.id)}
                            >
                              <i className="fas fa-check"></i>
                            </button>
                          )}
                          <Link
                            className="action-btn action-edit"
                            data-tip={Translate({ id: "edit" })}
                            to={`/manage/ai/edit/${item.id}`}
                          >
                            <i className="fas fa-pencil-alt"></i>
                          </Link>
                          <button
                            type="button"
                            className="action-btn action-delete"
                            data-tip={Translate({ id: "delete" })}
                            onClick={() => handleDelete(item)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="100">
                      <NoData />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </ManageDataTable>
        )}
      </div>
    </Layout>
  );
}

export default AiCredentials;
